import { TEST_ID_ORGANIZATION } from "e2e/testId";
import { Link } from "expo-router";
import { LogInIcon, PencilIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Button } from "@/components/atoms";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { InvitationsOrganizationFragment } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Props for the OrganizationItemView component.
 */
interface Props {
  item: InvitationsOrganizationFragment;
  loading: boolean;
  onLogInto: () => void;
  isAdmin: boolean;
  isDark: boolean;
}

/**
 * View component for displaying a single organization item with login and edit actions.
 * @param root0 - The component props
 * @param root0.item - The organization data fragment containing organization name and details
 * @param root0.loading - Boolean indicating if organization login operation is in progress
 * @param root0.onLogInto - Callback function to handle logging into the organization
 * @param root0.isAdmin - Boolean indicating if the current user has admin privileges
 * @param root0.isDark - Boolean indicating if dark theme is active for styling purposes
 */
const OrganizationItemView = ({
  item,
  loading,
  onLogInto,
  isAdmin,
  isDark,
}: Props) => (
  <Stack className="p-3">
    <Stack
      direction="horizontal"
      className="w-full items-center justify-between gap-3"
    >
      <Text
        variant="unstyled"
        className={`font-heading ${ifWeb("text-title-lg", "text-display-sm")} ${isDark ? "color-white" : "color-black"}`}
      >
        {item.name}
      </Text>

      <Stack direction="horizontal" className="items-center gap-6">
        <Button
          testID={TEST_ID_ORGANIZATION.SWITCH_ORGANIZATION_BUTTON}
          variant="link"
          onPress={onLogInto}
          isDisabled={loading}
          icon={LogInIcon}
          iconOnly
          iconSize={ifWeb("md", "lg")}
          accessibilityLabel="Switch to organization"
        />
        {item.name !== "Default" && isAdmin && (
          <Link asChild href={`/organizations/${item.id}/edit`}>
            <Button
              testID={TEST_ID_ORGANIZATION.EDIT_BUTTON}
              variant="link"
              isDisabled={loading}
              icon={PencilIcon}
              iconOnly
              iconSize={ifWeb("md", "lg")}
              accessibilityLabel="Edit organization"
            />
          </Link>
        )}
      </Stack>
    </Stack>
  </Stack>
);

OrganizationItemView.displayName = "OrganizationItemView";

export default memo(OrganizationItemView);
